import {Box, HStack, IconButton, Text, Tooltip} from "@chakra-ui/react";
import React, {useEffect, useMemo, useRef} from "react";
import {FaBold, FaItalic, FaListUl} from "react-icons/fa";

type Props = {
	value?: string;
	onChange: (value: string) => void;
	onBlur?: (value: string) => void;
	placeholder?: string;
	isDisabled?: boolean;
	minHeight?: string;
}

const normalizeRichText = (value?: string) => {
	const html = (value || "").trim();
	if (!html) {
		return "";
	}

	if (typeof document === "undefined") {
		return html;
	}

	const container = document.createElement("div");
	container.innerHTML = html;
	const textContent = (container.textContent || "").replace(/\u00a0/g, " ").trim();
	if (!textContent) {
		return "";
	}

	return container.innerHTML.trim();
};

const RichTextEditor = ({value, onChange, onBlur, placeholder, isDisabled = false, minHeight = "160px"}: Props) => {
	const editorRef = useRef<HTMLDivElement | null>(null);
	const savedRangeRef = useRef<Range | null>(null);
	const isToolbarInteractionRef = useRef<boolean>(false);

	const normalizedValue = useMemo(() => normalizeRichText(value), [value]);

	useEffect(() => {
		if (!editorRef.current) {
			return;
		}
		if (editorRef.current.innerHTML !== normalizedValue) {
			editorRef.current.innerHTML = normalizedValue;
		}
	}, [normalizedValue]);

	const selectionBelongsToEditor = (selection: Selection | null) => {
		if (!selection || selection.rangeCount === 0 || !editorRef.current) {
			return false;
		}
		const range = selection.getRangeAt(0);
		const commonAncestor = range.commonAncestorContainer;
		return editorRef.current.contains(commonAncestor.nodeType === Node.TEXT_NODE ? commonAncestor.parentNode : commonAncestor);
	};

	const saveSelection = () => {
		const selection = window.getSelection();
		if (!selectionBelongsToEditor(selection)) {
			return;
		}
		savedRangeRef.current = selection?.getRangeAt(0).cloneRange() || null;
	};

	const restoreSelection = () => {
		if (!savedRangeRef.current) {
			editorRef.current?.focus();
			return;
		}
		const selection = window.getSelection();
		if (!selection) {
			return;
		}
		selection.removeAllRanges();
		selection.addRange(savedRangeRef.current);
		editorRef.current?.focus();
	};

	const emitCurrentValue = () => {
		onChange(normalizeRichText(editorRef.current?.innerHTML));
	};

	const executeCommand = (command: string) => {
		if (isDisabled) {
			return;
		}
		restoreSelection();
		document.execCommand(command, false);
		saveSelection();
		emitCurrentValue();
	};

	const toolbarButtons = [
		{label: "Bold", command: "bold", icon: <FaBold/>},
		{label: "Italic", command: "italic", icon: <FaItalic/>},
		{label: "Bullets", command: "insertUnorderedList", icon: <FaListUl/>}
	];

	return <Box>
		<HStack spacing={1} marginBottom={2}>
			{toolbarButtons.map((button) => (
				<Tooltip key={button.command} label={button.label}>
					<IconButton
						aria-label={button.label}
						icon={button.icon}
						size={"xs"}
						variant={"outline"}
						onMouseDown={(event) => {
							event.preventDefault();
							isToolbarInteractionRef.current = true;
							executeCommand(button.command);
							window.setTimeout(() => {
								isToolbarInteractionRef.current = false;
							}, 0);
						}}
						isDisabled={isDisabled}
					/>
				</Tooltip>
			))}
		</HStack>
		<Text fontSize={"sm"} color={"gray.600"} marginBottom={2}>
			Formatted text is saved when the editor loses focus.
		</Text>
		<Box position={"relative"}>
			{!normalizedValue && placeholder ? (
				<Text
					position={"absolute"}
					top={"8px"}
					left={"12px"}
					color={"gray.400"}
					pointerEvents={"none"}
					fontSize={"sm"}
				>
					{placeholder}
				</Text>
			) : null}
			<div
				ref={editorRef}
				contentEditable={!isDisabled}
				suppressContentEditableWarning={true}
				onFocus={saveSelection}
				onKeyUp={saveSelection}
				onMouseUp={saveSelection}
				onInput={() => {
					saveSelection();
					emitCurrentValue();
				}}
				onBlur={() => {
					if (isToolbarInteractionRef.current) {
						return;
					}
					onBlur?.(normalizeRichText(editorRef.current?.innerHTML));
				}}
				style={{
					minHeight,
					padding: "8px 12px",
					border: "1px solid #CBD5E0",
					borderRadius: "6px",
					backgroundColor: isDisabled ? "#F7FAFC" : "#FFFFFF",
					overflowY: "auto"
				}}
			/>
		</Box>
	</Box>;
};

export default RichTextEditor;
